import React, { forwardRef, Component } from 'react';
import styled, { css, keyframes } from 'styled-components';
import propTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import universityCodes from '@utility/universityCodes';
import {
  DateIcon,
  RightArrowIcon,
  LeftArrowIcon,
} from '../../../../../../editJournal/toolset/assets/icons';
import PlaceHolder from '../../../../../../UI/Editor/EditSpace/EditorPane/RenderContent/placeholder';
import { toDates } from '../../../../../../../../../constants/resumeNodes';
import monthsDict from '../../../../../../Export/Resume/common/generator/monthsDict';
import {
  BrandeisBlue,
  PrimaryDark,
  SonicSilver,
  white,
} from '../../../../../../../../../styling/theme';
import {
  fnBrowserDetect,
  isSafari,
} from '../../../../../../../../../utility/browsers';
import getSelectedYearBG from './getSelectedYearBG';
import breakpointsMinWidth from '../../../../../../../../../styling/breakpointsMinWidth';
import { SAFARI } from '../../../../../../../../../utility/enums/browsers';

let color;
const returnDateStyle = ({ isFocused }) => {
  if (isFocused) {
    color = PrimaryDark;
    return css`
      border: 1px solid rgba(133, 16, 223, 1);
      box-sizing: border-box;
      border-radius: 4px;
    `;
  }

  color = '#828282';
  return null;
};

const FromWrapper = styled.div`
  border: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 10px;
  position: relative;
  caret-color: transparent !important;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #333d47;
  pointer-events: auto;
  cursor: pointer;
  min-width: 110px;

  ${(props) => returnDateStyle(props)}

  ${(props) => {
    const { dateType } = props;

    if (dateType === 'From') {
      return css`
        grid-column: 3/4;
      `;
    }

    if (dateType === 'To') {
      return css`
        grid-column: 4/-1;
      `;
    }

    return null;
  }}

  ${(props) => {
    const { nodeType, universityPlan } = props;

    if (
      nodeType === 'awards_and_honors_issue_date' ||
      nodeType === 'courses_completion_date' ||
      nodeType === 'conferences_date'
    ) {
      return css`
        grid-column: 4/-1;
        min-width: 200px;
      `;
    }
    if (nodeType === 'test_scores_date') {
      return css`
        grid-column: 1 / span 2;
      `;
    }
    if (
      nodeType === 'associations_from' ||
      nodeType === 'associations_to' ||
      nodeType === 'volunteer_work_from' ||
      nodeType === 'volunteer_work_to' ||
      nodeType === 'leadership_from' ||
      nodeType === 'leadership_to' ||
      nodeType === 'projects_from' ||
      nodeType === 'projects_to'
    ) {
      return css`
        grid-row: 1 / -1;
      `;
    }

    if (
      universityPlan?.code === universityCodes.OREGONSTATE_EDU &&
      nodeType === 'education_to'
    ) {
      return css`
        grid-column: 3 / -1;
      `;
    }
    return css``;
  }}

@media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 0 16px;
  }

  @media ${breakpointsMinWidth.tabletSm} {
    height: 36px;
    padding: 0 9px;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 0 10px;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    height: 40px;
    padding: 0 14px;
    font-size: 14px;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Picker = styled.span`
  pointer-events: auto;
  cursor: pointer;
  position: absolute;
  width: 100%;
  padding: 0 14px;
  user-select: none;
  left: 10px;
  @media ${breakpointsMinWidth.mobileMicroLg} {
    left: 12px;
  }
  @media ${breakpointsMinWidth.tabletSm} {
    left: 11px;
  }
  @media ${breakpointsMinWidth.tabletMd} {
    left: 8px;
  }
  @media ${breakpointsMinWidth.tabletLg} {
    left: 10px;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    left: 14px;
  }

  .react-datepicker {
    --calendar-shadow:
      0px 0px 15px 0px rgba(0, 0, 0, 0.03),
      0px 2px 30px 0px rgba(0, 0, 0, 0.08), 0px 0px 1px 0px rgba(0, 0, 0, 0.3);
    width: 192px;
    height: ${(props) => (props.showMonthYearPicker ? '210px' : '188px')};
    background: hsl(0 0% 98.04%);
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      var(--calendar-shadow);
    border-radius: 14px;
    border: none;
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    z-index: 2;
    animation: ${fadeIn} 0.3s ease;
    @media ${breakpointsMinWidth.desktopMd} {
      width: 256px;
      height: ${(props) => (props.showMonthYearPicker ? '264px' : '234px')};
      font-size: 14px;
    }
  }

  .react-datepicker-popper {
    left: auto;
  }

  .react-datepicker-popper[data-placement='bottom-end']
    .react-datepicker__triangle,
  .react-datepicker-popper[data-placement='top-end']
    .react-datepicker__triangle {
    display: none;
  }
  .react-datepicker-popper[data-placement='bottom-end']
    .react-datepicker__triangle {
    border-bottom-color: rgba(71, 56, 128, 1);
  }

  .react-datepicker-popper[data-placement^='bottom'] {
    margin-top: 0px;
  }

  .react-datepicker-popper[data-placement^='top'] {
    margin-bottom: 0px;
  }

  .react-datepicker__header {
    background: ${white};
    padding-top: 8px;
    height: 40px;
    border: none;
    border-radius: 14px 14px 0 0;
    --tw-shadow: 0px 20px 20px 0px rgb(0 0 0/0.05);
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      0px 20px 20px 0px rgb(0 0 0/0.05);
  }
  .react-datepicker__month {
    align-items: center;
    margin: 8px;
    text-align: center;
    display: inline-flex;
    display: flex;
    flex-direction: column;
    grid-template-columns: repeat(4, 1fr);

    div {
      display: flex;
      width: 100%;
      justify-content: space-around;
      align-items: center;
      gap: 4px;
    }
  }
  .react-datepicker__month-container {
    width: 100%;
  }
  .react-datepicker__year-container {
    align-items: center;
    margin: 0.498rem;
    text-align: center;
    display: inline-flex;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }
  .react-datepicker__month-text.react-datepicker__month--selected:hover {
    background: ${BrandeisBlue};
    color: #fff;
  }
  .react-datepicker__month-text.react-datepicker__month--in-range:hover {
    background: #ffffff;
    box-shadow: 0px 0px 4px #cbcbcb;
  }
  .react-datepicker__year-container-text {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    width: 32px;
    margin: 0 auto;
    border-radius: 50%;
    transition-property: transform, background-color, color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 0.3s;

    flex: 1 0 20%;
    @media ${breakpointsMinWidth.desktopMd} {
      height: 44px;
      width: 44px;
    }
  }
  .react-datepicker__month .react-datepicker__month-text,
  .react-datepicker__month .react-datepicker__quarter-text {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin: 0;
    border-radius: 50%;
    transition-property: transform, background-color, color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 0.3s;

    @media ${breakpointsMinWidth.desktopMd} {
      height: 40px;
      width: 40px;
    }
  }
  .react-datepicker__month-text:hover,
  .react-datepicker__year-container-text:hover {
    background: #e6f1fe;
    color: #338ef7;
  }

  .react-datepicker__year-container-text.selected {
    background: ${BrandeisBlue};
    color: #fff;
  }
  .react-datepicker__month--selected {
    background: ${BrandeisBlue};
    color: #fff;
  }
  .react-datepicker-wrapper {
    width: inherit;
    .react-datepicker__input-container {
      vertical-align: middle;
    }
    .react-datepicker__month-wrapper {
      height: 140px;
    }
  }

  @media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 0 16px;
  }

  @media ${breakpointsMinWidth.tabletSm} {
    padding: 0 9px;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 0 10px;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    padding: 0 14px;
  }
`;

const DateButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  outline-style: none;
`;

const Value = styled.span``;

const DateIconWrapper = styled.span`
  color: #828282;
  margin-right: 10px;
  display: flex;
  align-items: center;
  @media ${breakpointsMinWidth.mobileMicroLg} {
    margin-right: 12px;
  }
  @media ${breakpointsMinWidth.tabletSm} {
    margin-right: 11px;
  }
  @media ${breakpointsMinWidth.tabletMd} {
    margin-right: 8px;
  }
  @media ${breakpointsMinWidth.tabletLg} {
    margin-right: 10px;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    margin-right: 14px;
  }
`;

const DateButtons = styled.button`
  width: 80px;
  font-size: 12px;
  background: none;
  border-radius: 4px;
  color: #11181c;
  padding: 4px;
  transition-property: transform, color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 0.3s;

  &:hover {
    color: #338ef7;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    font-size: 14px;
  }
`;

const PlaceHolder2 = styled(PlaceHolder)`
  ${(props) => {
    const { dateInput, isCurrent } = props;
    if (!dateInput && !isCurrent) {
      return css`
        top: 50%;
      `;
    }
    return null;
  }}
`;

const DatepickerHeaderButton = styled.button`
  background: ${white};
  color: ${SonicSilver};
  font-size: 14px;
  font-weight: 500;
`;

const DatepickerArrowButton = styled.button`
  background: ${white};
  border-radius: 50%;
  transition-property: transform, background-color, color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 0.3s;
  &:hover {
    background: rgba(212, 212, 216, 0.4);
  }
`;

const DatepickerHeader = styled.div`
  padding: 0 16px;
  border-radius: 8px;
  background: ${white};
  display: flex;
  justify-content: space-between;
  cursor: default;
`;

const Clear = styled(DateButtons)`
  color: #f31260;
  &:hover {
    font-weight: 500;
    color: #f31260;
  }
`;

const Current = styled(DateButtons)`
  color: #338ef7;
  &:hover {
    font-weight: 500;
    color: #338ef7;
  }
`;

const DateInput = forwardRef((props, ref) => {
  const { value, onClick, dateInput, isCurrent, editor, node, setAnimate } =
    props;
  const timeStamp = Date.parse(value);
  const date = new Date(timeStamp);
  const month = monthsDict[date.getMonth()];
  const year = date.getFullYear();
  let formattedDate = `${month} ${year}`;
  if (formattedDate === 'undefined NaN') {
    formattedDate = dateInput;
  }

  const dateValue = isCurrent ? 'Current*' : formattedDate;

  const clickHandler = (e) => {
    if (window.env !== 'test' && fnBrowserDetect() !== SAFARI)
      editor.moveToStartOfNode(node).focus();
    onClick(e);
    setAnimate(true);
    setTimeout(() => {
      setAnimate(false);
    }, 300);
  };
  return (
    <DateButton
      onClick={clickHandler}
      onKeyDown={(e) => e.preventDefault()}
      tabIndex="0"
      contentEditable={false}
      dateValue={dateValue}
      ref={ref}
      data-testid="custom_input"
    >
      <Value>{dateValue}</Value>
      <DateIconWrapper>
        <DateIcon fill={color} width="12px" height="12px" />
      </DateIconWrapper>
    </DateButton>
  );
});

class DateComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showYearPicker: true,
      animate: false,
    };

    this.dateRef = React.createRef();
  }

  componentDidMount = () => {
    const { setDateRef } = this.props;
    const {
      nodeProps: { node, editor },
    } = this.props;

    const { data } = node;
    const nodeDateInput = data.get('dateInput');
    if (nodeDateInput) {
      this.setState({
        showYearPicker: false,
      });
    }

    document.addEventListener('click', (e) => {
      if (
        e.target.classList.contains('react-datepicker__year-container-text')
      ) {
        const { key: nodeKey } = node;
        const { current } = this.dateRef;
        const {
          value: { focusBlock },
        } = editor;
        if (nodeKey && nodeKey === focusBlock.key) {
          this.setState({ showYearPicker: false });
          if (current) {
            current.setOpen(true);
          }
        }
      }

      if (e.target.classList.contains('formWrapper')) {
        const { key: nodeKey } = node;
        const { current } = this.dateRef;
        const {
          value: { focusBlock },
        } = editor;
        if (node && focusBlock && nodeKey === focusBlock.key) {
          if (current && !current.state.open) {
            current.setOpen(true);
          }
        }
      }
    });

    setDateRef(node.key, this.dateRef);
  };

  updateDate = (value) => {
    const {
      nodeProps: { editor, node },
    } = this.props;

    const {
      value: { document },
    } = editor;

    const previousBlock = document.getPreviousBlock(node.key);

    if (previousBlock) {
      editor.moveToStartOfNode(previousBlock).focus();
    }
    const updatedDate = new Date(value);
    const formattedDate = updatedDate.toLocaleDateString('en-US');
    editor.moveToStartOfNode(node).setNodeByKey(node.key, {
      data: {
        dateInput: formattedDate,
        current: false,
      },
    });
  };

  onClear = () => {
    const {
      nodeProps: { editor, node },
    } = this.props;
    editor
      .moveToStartOfNode(node)
      .setNodeByKey(node.key, {
        data: { dateInput: null },
      })
      .focus();
    this.setState({ showYearPicker: true });
  };

  setCurrent = () => {
    const {
      nodeProps: { editor, node },
    } = this.props;

    editor
      .moveToStartOfNode(node)
      .setNodeByKey(node.key, {
        data: {
          dateInput: null,
          current: true,
        },
      })
      .focus();
    const { key: nodeKey } = node;
    const { current } = this.dateRef;
    const {
      value: { focusBlock },
    } = editor;
    if (nodeKey && nodeKey === focusBlock.key) {
      if (current.state.open) {
        current.setOpen(false);
        this.setState({ showYearPicker: true });
      }
    }
  };

  isToDate = (type) => Object.values(toDates).includes(type);

  handleCalenderSwitch = (e, date, isCurrent) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({
      showYearPicker: !prevState.showYearPicker,
    }));
    getSelectedYearBG(date, isCurrent);
  };

  render() {
    const {
      nodeProps: { node, attributes, children, isFocused, editor },
      placeHolder,
      activePlan,
      subscriptionStatus,
      formatType,
      universityPlan,
    } = this.props;

    const { text, data, type } = node;
    const { showYearPicker, animate } = this.state;
    const nodeDateInput = data.get('dateInput');
    const dateInput = nodeDateInput ? new Date(nodeDateInput) : null;
    const isCurrent = data.get('current');

    return (
      <FromWrapper
        {...attributes}
        dateType={placeHolder}
        nodeType={type}
        id="from"
        isFocused={isFocused}
        isSafari={isSafari}
        className="formWrapper"
        onClick={() => {
          const { current } = this.dateRef;
          if (current && !current.state.open) {
            if (dateInput) {
              this.setState({
                showYearPicker: false,
              });
            } else {
              this.setState({
                showYearPicker: true,
              });
            }
          }
        }}
        universityPlan={universityPlan}
      >
        <Picker
          spellCheck={false}
          data-testid={`${type}_date_picker`}
          animate={animate}
          showMonthYearPicker={!showYearPicker}
        >
          <DatePicker
            ref={this.dateRef}
            selected={dateInput}
            onChange={this.updateDate}
            onFocus={(e) => {
              e.target.readOnly = true;
            }}
            renderCustomHeader={({ date, decreaseYear, increaseYear }) => (
              <DatepickerHeader>
                <DatepickerArrowButton
                  onClick={() => {
                    decreaseYear();
                    getSelectedYearBG(dateInput, isCurrent);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="text-default-400"
                >
                  <LeftArrowIcon fill="#a1a1aa" />
                </DatepickerArrowButton>
                <DatepickerHeaderButton
                  onClick={(e) =>
                    this.handleCalenderSwitch(e, dateInput, isCurrent)
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {showYearPicker
                    ? `${date.getFullYear() - 11} - ${date.getFullYear()}`
                    : `${date.getFullYear()}`}
                </DatepickerHeaderButton>

                <DatepickerArrowButton
                  onClick={() => {
                    increaseYear();
                    getSelectedYearBG(dateInput, isCurrent);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <RightArrowIcon fill="#a1a1aa" />
                </DatepickerArrowButton>
              </DatepickerHeader>
            )}
            customInput={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <DateInput
                dateInput={dateInput}
                isCurrent={isCurrent}
                nodeType={type}
                activePlan={activePlan}
                subscriptionStatus={subscriptionStatus}
                formatType={formatType}
                editor={editor}
                node={node}
                animate={animate}
                setAnimate={(animateVal) =>
                  this.setState({ animate: animateVal })
                }
              />
            }
            popperPlacement="bottom-end"
            popperModifiers={{
              offset: {
                enabled: true,
                offset: '0px, 16px',
              },
              preventOverflow: {
                enabled: true,
                escapeWithReference: false,
                boundariesElement: 'viewport',
              },
            }}
            showMonthYearPicker={!showYearPicker}
            showYearPicker={showYearPicker}
          >
            <div>
              <Current
                aria-label="Select Current Date"
                onClick={this.setCurrent}
              >
                Current
              </Current>
              <Clear
                aria-label="Clear Selection"
                onClick={() => {
                  this.onClear();
                  getSelectedYearBG(null, isCurrent);
                }}
              >
                Clear
              </Clear>
            </div>
          </DatePicker>
        </Picker>
        {children}
        <PlaceHolder2
          contentEditable={false}
          nodeText={text}
          dateInput={dateInput}
          isCurrent={isCurrent}
          isFocused={isFocused}
        >
          {placeHolder}
        </PlaceHolder2>
      </FromWrapper>
    );
  }
}

DateInput.propTypes = {
  value: propTypes.string,
  onClick: propTypes.func,
  dateInput: propTypes.string,
};

DateInput.defaultProps = {
  value: null,
  dateInput: null,
  onClick: () => {},
};

DateInput.displayName = 'DateInput';

DateComponent.propTypes = {
  nodeProps: propTypes.objectOf(propTypes.object).isRequired,
  attributes: propTypes.objectOf(propTypes.string).isRequired,
  value: propTypes.objectOf(propTypes.string).isRequired,
  onClick: propTypes.func.isRequired,
  dateType: propTypes.string.isRequired,
  placeHolder: propTypes.string.isRequired,
};

export default DateComponent;
